// src/components/layout/Navigation.tsx - ФИНАЛЬНОЕ ИСПРАВЛЕНИЕ

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const MIN_TOUCH_TARGET = 48;

// ✅ ИСПРАВЛЕНО: Упрощенное центрирование
const NavigationContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 8px 16px;
  min-height: 76px;
  background: var(--tg-theme-secondary-bg-color, #f1f1f1);
  border-top: 1px solid var(--tg-theme-secondary-bg-color, #e1e1e1);
  
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  max-width: 430px;
  z-index: 1000;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
  
  /* ✅ ИСПРАВЛЕНО: Простое центрирование через margin */
  margin: 0 auto;
  
  @media (min-width: 769px) {
    /* На больших экранах - строго по центру */
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }
`;

const NavItem = styled(motion.button)<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  border-radius: 8px;
  border: none;
  background: ${props => props.$isActive 
    ? 'var(--tg-theme-bg-color, #ffffff)' 
    : 'transparent'
  };
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 60px;
  min-height: ${MIN_TOUCH_TARGET}px;
  flex: 1;
  max-width: 90px;
  position: relative; /* ✅ ОСТАВЛЯЕМ для возможного будущего использования */
  
  &:hover {
    background: var(--tg-theme-bg-color, #ffffff);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    opacity: 0.8;
  }
  
  &:focus-visible {
    outline: 2px solid var(--tg-theme-button-color, #7B68EE);
    outline-offset: 2px;
  }
`;

const NavIcon = styled.div<{ $isActive: boolean }>`
  width: 28px;
  height: 28px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 22px;
    height: 22px;
    transition: all 0.2s ease;
    color: ${props => props.$isActive 
      ? 'var(--tg-theme-button-color, #7B68EE)' 
      : 'var(--tg-theme-hint-color, #64748B)'
    };
  }
`;

const NavLabel = styled.span<{ $isActive: boolean }>`
  font-size: 11px;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  text-align: center;
  line-height: 1.2;
  color: ${props => props.$isActive 
    ? 'var(--tg-theme-button-color, #7B68EE)' 
    : 'var(--tg-theme-text-color, #000000)'
  };
`;

// ❌ УДАЛЕНО: ActiveIndicator - глючит на мобилках, достаточно подсветки фона

interface NavItemType {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItemType[] =[
  {
    path: '/calendar',
    label: 'Календарь',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/>
        <circle cx="12" cy="15" r="2" fill="currentColor"/>
      </svg>
    ),
  },
  {
    path: '/achievements',
    label: 'Достижения',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/>
      </svg>
    ),
  },
  {
    path: '/profile',
    label: 'Профиль',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
      </svg>
    ),
  },
];

interface NavigationProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export default function Navigation({ currentPath, onNavigate }: NavigationProps) {
  const isActiveTab = (path: string) => {
    if (path === '/calendar') {
      return currentPath === '/' || currentPath === '/calendar';
    }
    return currentPath.startsWith(path);
  };

  const handleNavClick = (path: string) => {
    if (navigator.vibrate) {
      navigator.vibrate([10]);
    }
    onNavigate(path);
  };

  return (
    <NavigationContainer>
      {NAV_ITEMS.map((item, index) => {
        const isActive = isActiveTab(item.path);
        
        return (
          <NavItem
            key={item.path}
            $isActive={isActive}
            onClick={() => handleNavClick(item.path)}
            whileHover={{ 
              y: -1,
              transition: { duration: 0.2 }
            }}
            whileTap={{ 
              scale: 0.95,
              transition: { duration: 0.1 }
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.3, 
              delay: index * 0.1,
              ease: "easeOut"
            }}
          >
            {/* ❌ УДАЛЕНО: <ActiveIndicator /> - глючит, не нужна */}
            
            <NavIcon $isActive={isActive}>
              {item.icon}
            </NavIcon>
            
            <NavLabel $isActive={isActive}>
              {item.label}
            </NavLabel>
          </NavItem>
        );
      })}
    </NavigationContainer>
  );
}